import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ApiError, api } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  type BondingRoutingStrategy,
  type CreateLlmBondingRequest,
  CreateLlmBondingRequestSchema,
  type LlmBonding,
  type LlmBondingProbeMemberResult,
  type LlmEndpoint,
  type UpdateLlmBondingRequest,
  UpdateLlmBondingRequestSchema,
} from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GripVertical } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Modal } from './Modal';

type Mode = { kind: 'create' } | { kind: 'edit'; bonding: LlmBonding };

interface Props {
  open: boolean;
  mode: Mode | null;
  onClose: () => void;
}

interface FormState {
  name: string;
  displayName: string;
  description: string;
  memberIds: string[];
  routingStrategy: BondingRoutingStrategy;
  isEnabled: boolean;
  isEnabledForUsers: boolean;
  isDefault: boolean;
  isVisionDefault: boolean;
  isAudioDefault: boolean;
  sortOrder: string;
  relayAlias: string;
}

const EMPTY: FormState = {
  name: '',
  displayName: '',
  description: '',
  memberIds: [],
  routingStrategy: 'random',
  isEnabled: true,
  isEnabledForUsers: true,
  isDefault: false,
  isVisionDefault: false,
  isAudioDefault: false,
  sortOrder: '0',
  relayAlias: '',
};

export function BondingFormModal({ open, mode, onClose }: Props) {
  if (!open || !mode) return null;
  return mode.kind === 'create' ? (
    <FormBody onClose={onClose} kind="create" />
  ) : (
    <FormBody onClose={onClose} kind="edit" bonding={mode.bonding} />
  );
}

type FormBodyProps =
  | { onClose: () => void; kind: 'create' }
  | { onClose: () => void; kind: 'edit'; bonding: LlmBonding };

function FormBody(props: FormBodyProps) {
  const qc = useQueryClient();
  const isEdit = props.kind === 'edit';
  const bonding = isEdit ? props.bonding : null;

  const initialState: FormState = useMemo(() => {
    if (!bonding) return EMPTY;
    return {
      name: bonding.name,
      displayName: bonding.displayName ?? '',
      description: bonding.description ?? '',
      memberIds: bonding.memberIds,
      routingStrategy: bonding.routingStrategy,
      isEnabled: bonding.isEnabled,
      isEnabledForUsers: bonding.isEnabledForUsers,
      isDefault: bonding.isDefault,
      isVisionDefault: bonding.isVisionDefault,
      isAudioDefault: bonding.isAudioDefault,
      sortOrder: String(bonding.sortOrder),
      relayAlias: bonding.relayAlias ?? '',
    };
  }, [bonding]);

  const [form, setForm] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [probeResults, setProbeResults] = useState<LlmBondingProbeMemberResult[] | null>(null);

  // 멤버 후보 — 전체 endpoint 목록 (노출 여부 무관, 관리자니까).
  const endpointsQuery = useQuery({
    queryKey: ['admin', 'llm-endpoints'],
    queryFn: () => api.adminLlmList(),
  });
  const endpoints: LlmEndpoint[] = endpointsQuery.data?.endpoints ?? [];

  const createMut = useMutation({
    mutationFn: (body: CreateLlmBondingRequest) => api.adminLlmBondingCreate(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'llm-bondings'] });
      qc.invalidateQueries({ queryKey: ['admin', 'llm-endpoints'] });
      props.onClose();
    },
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '생성에 실패했습니다.');
    },
  });

  const updateMut = useMutation({
    mutationFn: (body: UpdateLlmBondingRequest) => {
      if (!bonding) throw new Error('edit 모드가 아닙니다.');
      return api.adminLlmBondingUpdate(bonding.id, body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'llm-bondings'] });
      qc.invalidateQueries({ queryKey: ['admin', 'llm-endpoints'] });
      props.onClose();
    },
    onError: (err: unknown) => {
      setServerError(err instanceof ApiError ? err.message : '수정에 실패했습니다.');
    },
  });

  const probeMut = useMutation({
    mutationFn: async () => {
      if (form.memberIds.length === 0) throw new Error('멤버를 먼저 선택하세요.');
      return api.adminLlmBondingProbeDraft({ memberIds: form.memberIds });
    },
    onSuccess: (r) => setProbeResults(r.members),
    onError: (err: unknown) => {
      setServerError(err instanceof Error ? err.message : '연결 테스트 실패');
    },
  });

  const toggleMember = (id: string) => {
    setForm((f) => ({
      ...f,
      memberIds: f.memberIds.includes(id)
        ? f.memberIds.filter((x) => x !== id)
        : [...f.memberIds, id],
    }));
    setProbeResults(null);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onMemberDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    setForm((f) => {
      const oldIndex = f.memberIds.indexOf(String(active.id));
      const newIndex = f.memberIds.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return f;
      return { ...f, memberIds: arrayMove(f.memberIds, oldIndex, newIndex) };
    });
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setServerError(null);
    setErrors({});

    const sortOrder = Number.parseInt(form.sortOrder, 10);
    if (Number.isNaN(sortOrder)) {
      setErrors({ sortOrder: '숫자를 입력하세요.' });
      return;
    }

    const aliasTrimmed = form.relayAlias.trim();

    if (!isEdit) {
      const body: CreateLlmBondingRequest = {
        name: form.name,
        displayName: form.displayName.trim() || null,
        description: form.description.trim() || null,
        memberIds: form.memberIds,
        routingStrategy: form.routingStrategy,
        isEnabled: form.isEnabled,
        isEnabledForUsers: form.isEnabledForUsers,
        isDefault: form.isDefault,
        isVisionDefault: form.isVisionDefault,
        isAudioDefault: form.isAudioDefault,
        sortOrder,
        relayAlias: aliasTrimmed || null,
      };
      const parsed = CreateLlmBondingRequestSchema.safeParse(body);
      if (!parsed.success) {
        setErrors(zodIssuesToMap(parsed.error.issues));
        return;
      }
      createMut.mutate(parsed.data);
      return;
    }

    const body: UpdateLlmBondingRequest = {
      name: form.name,
      displayName: form.displayName.trim() || null,
      description: form.description.trim() || null,
      memberIds: form.memberIds,
      routingStrategy: form.routingStrategy,
      isEnabled: form.isEnabled,
      isEnabledForUsers: form.isEnabledForUsers,
      isDefault: form.isDefault,
      isVisionDefault: form.isVisionDefault,
      sortOrder,
      relayAlias: aliasTrimmed || null,
    };
    const parsed = UpdateLlmBondingRequestSchema.safeParse(body);
    if (!parsed.success) {
      setErrors(zodIssuesToMap(parsed.error.issues));
      return;
    }
    updateMut.mutate(parsed.data);
  };

  const pending = createMut.isPending || updateMut.isPending;
  const title = isEdit ? '본딩 수정' : '본딩 추가';

  const footer = (
    <div className="flex justify-between gap-2">
      <Button
        type="button"
        variant="ghost"
        disabled={probeMut.isPending || form.memberIds.length === 0}
        onClick={() => probeMut.mutate()}
      >
        {probeMut.isPending ? '테스트 중…' : '멤버 연결 테스트'}
      </Button>
      <div className="flex gap-2">
        <Button type="button" variant="ghost" onClick={props.onClose}>
          취소
        </Button>
        <Button type="submit" form="bonding-form" disabled={pending}>
          {pending ? '저장 중…' : '저장'}
        </Button>
      </div>
    </div>
  );

  const selectedSet = new Set(form.memberIds);
  const probeByMember = new Map((probeResults ?? []).map((r) => [r.memberId, r]));
  const endpointById = new Map(endpoints.map((e) => [e.id, e]));
  const isPriority = form.routingStrategy === 'priority';

  return (
    <Modal
      open
      onClose={props.onClose}
      title={title}
      description={isEdit ? bonding?.name : '여러 LLM 엔드포인트를 하나의 가상 LLM 으로 묶습니다.'}
      size="lg"
      footer={footer}
    >
      <form onSubmit={submit} id="bonding-form" className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="표시 이름"
            htmlFor="bd-display-name"
            hint="사용자 드롭다운 표시명"
            error={errors.displayName}
          >
            <Input
              id="bd-display-name"
              value={form.displayName}
              onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
              placeholder="균등 분산 LLM"
            />
          </Field>
          <Field label="내부 이름" htmlFor="bd-name" hint="관리자 식별용" error={errors.name}>
            <Input
              id="bd-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="default-bond"
            />
          </Field>
        </div>

        <Field label="설명 (선택)" htmlFor="bd-description" error={errors.description}>
          <Input
            id="bd-description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Haiku + Sonnet 랜덤 분배"
          />
        </Field>

        <Field
          label="라우팅 전략"
          htmlFor="bd-strategy"
          hint={
            isPriority
              ? '우선순위 순서대로 위에서부터 사용합니다. 상위 엔드포인트가 슬롯 full·응답 불가일 때 다음 순서로 내려갑니다 (예: 내부 GPU → 외부 API).'
              : '체크한 엔드포인트 중에서 매 메시지마다 랜덤 1개가 균등 선택됩니다.'
          }
        >
          <select
            id="bd-strategy"
            value={form.routingStrategy}
            onChange={(e) =>
              setForm((f) => ({ ...f, routingStrategy: e.target.value as BondingRoutingStrategy }))
            }
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm focus:border-accent focus:outline-none"
          >
            <option value="random">랜덤 균등 분배</option>
            <option value="priority">우선순위 (위에서부터)</option>
          </select>
        </Field>

        <Field
          label={`멤버 엔드포인트 (현재 ${form.memberIds.length}개 — 최소 2개)`}
          htmlFor="bd-members"
          hint={
            isPriority
              ? '체크해서 멤버를 추가하고, 아래 "우선순위 순서" 목록에서 드래그로 순서를 정하세요.'
              : '체크한 엔드포인트 중에서 매 메시지마다 랜덤 1개가 선택됩니다.'
          }
          error={errors.memberIds}
        >
          <div className="max-h-64 overflow-y-auto rounded-md border border-border bg-surface divide-y divide-border">
            {endpointsQuery.isLoading ? (
              <div className="px-3 py-4 text-sm text-foreground-muted">불러오는 중…</div>
            ) : endpoints.length === 0 ? (
              <div className="px-3 py-4 text-sm text-foreground-muted">
                등록된 엔드포인트가 없습니다. 먼저 엔드포인트를 추가하세요.
              </div>
            ) : (
              endpoints.map((ep) => {
                const checked = selectedSet.has(ep.id);
                const probe = probeByMember.get(ep.id);
                return (
                  <label
                    key={ep.id}
                    htmlFor={`bd-m-${ep.id}`}
                    className={cn(
                      'flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-surface-muted/30',
                      checked && 'bg-surface-muted/30',
                    )}
                  >
                    <input
                      id={`bd-m-${ep.id}`}
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleMember(ep.id)}
                      className="size-4"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">
                          {ep.displayName ?? ep.name}
                        </span>
                        <span className="text-[11px] uppercase tracking-wider text-foreground-muted">
                          {ep.provider}
                        </span>
                      </div>
                      <div className="mt-0.5 text-[11px] text-foreground-muted font-mono truncate">
                        {ep.model}
                      </div>
                    </div>
                    {probe ? (
                      <span
                        className={cn(
                          'text-[11px] rounded-sm px-1.5 h-5 inline-flex items-center',
                          probe.ok
                            ? 'bg-success/15 text-success'
                            : 'bg-destructive/15 text-destructive',
                        )}
                        title={probe.error ?? `${probe.models.length}개 모델`}
                      >
                        {probe.ok ? `${probe.models.length} 모델` : '실패'}
                      </span>
                    ) : null}
                  </label>
                );
              })
            )}
          </div>
        </Field>

        {isPriority && form.memberIds.length > 0 ? (
          <Field
            label="우선순위 순서 (위 = 최우선)"
            htmlFor="bd-member-order"
            hint="드래그로 순서를 정하세요. 맨 위 엔드포인트부터 사용하고, 사용 불가 시 아래로 내려갑니다."
          >
            <div
              id="bd-member-order"
              className="rounded-md border border-border bg-surface divide-y divide-border"
            >
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={onMemberDragEnd}
              >
                <SortableContext items={form.memberIds} strategy={verticalListSortingStrategy}>
                  {form.memberIds.map((id, idx) => {
                    const ep = endpointById.get(id);
                    return (
                      <SortableMemberRow
                        key={id}
                        id={id}
                        rank={idx + 1}
                        label={ep ? (ep.displayName ?? ep.name) : id}
                        provider={ep?.provider ?? ''}
                        model={ep?.model ?? ''}
                        onRemove={() => toggleMember(id)}
                      />
                    );
                  })}
                </SortableContext>
              </DndContext>
            </div>
          </Field>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Sort order" htmlFor="bd-sort-order" error={errors.sortOrder}>
            <Input
              id="bd-sort-order"
              type="number"
              value={form.sortOrder}
              onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
            />
          </Field>
          <Field
            label="API 릴레이 표시 이름"
            htmlFor="bd-relay-alias"
            hint="외부에서 model 필드에 넣을 값. 예: gemma4. 엔드포인트 alias 와 통틀어 unique. 비우면 릴레이 미노출."
            error={errors.relayAlias}
          >
            <Input
              id="bd-relay-alias"
              value={form.relayAlias}
              onChange={(e) => setForm((f) => ({ ...f, relayAlias: e.target.value }))}
              placeholder="gemma4"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />
          </Field>
        </div>

        <div className="space-y-2 pt-1">
          <Checkbox
            id="bd-is-enabled"
            checked={form.isEnabled}
            onChange={(v) => setForm((f) => ({ ...f, isEnabled: v }))}
            label="본딩 활성 (비활성 시 사용자 드롭다운에서 숨김)"
          />
          <Checkbox
            id="bd-enabled-users"
            checked={form.isEnabledForUsers}
            onChange={(v) => setForm((f) => ({ ...f, isEnabledForUsers: v }))}
            label="사용자 드롭다운에 노출"
          />
          <Checkbox
            id="bd-is-default"
            checked={form.isDefault}
            onChange={(v) => setForm((f) => ({ ...f, isDefault: v }))}
            label="기본 LLM 으로 설정 (endpoint 와 bonding 통틀어 1개)"
          />
          <Checkbox
            id="bd-is-vision-default"
            checked={form.isVisionDefault}
            onChange={(v) => setForm((f) => ({ ...f, isVisionDefault: v }))}
            label="기본 Vision 모델로 설정 (멤버 모두 Vision 지원이어야 함)"
          />
          {form.isVisionDefault &&
          form.memberIds.some((id) => !endpoints.find((e) => e.id === id)?.supportsVision) ? (
            <p className="ml-6 text-xs text-amber-600">
              ⚠ 일부 멤버가 Vision 미지원입니다. 저장 시 검증에서 실패합니다 — 멤버 endpoint 의
              "Vision 지원" 을 먼저 켜세요.
            </p>
          ) : null}
          <Checkbox
            id="bd-is-audio-default"
            checked={form.isAudioDefault}
            onChange={(v) => setForm((f) => ({ ...f, isAudioDefault: v }))}
            label="기본 Audio 모델로 설정 (멤버 모두 Audio 지원이어야 함)"
          />
          {form.isAudioDefault &&
          form.memberIds.some((id) => !endpoints.find((e) => e.id === id)?.supportsAudio) ? (
            <p className="ml-6 text-xs text-amber-600">
              ⚠ 일부 멤버가 Audio 미지원입니다. 저장 시 검증에서 실패합니다 — 멤버 endpoint 의
              "Audio 지원" 을 먼저 켜세요.
            </p>
          ) : null}
        </div>

        {serverError ? <p className="text-sm text-destructive">{serverError}</p> : null}
      </form>
    </Modal>
  );
}

function SortableMemberRow({
  id,
  rank,
  label,
  provider,
  model,
  onRemove,
}: {
  id: string;
  rank: number;
  label: string;
  provider: string;
  model: string;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 bg-surface px-3 py-2 first:rounded-t-md last:rounded-b-md"
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label="순서 변경"
        className="flex size-7 shrink-0 cursor-grab items-center justify-center rounded text-foreground-faint hover:bg-surface-muted hover:text-foreground active:cursor-grabbing"
      >
        <GripVertical className="size-4" />
      </button>
      <span className="w-5 shrink-0 text-center text-xs font-semibold text-foreground-muted">
        {rank}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{label}</span>
          <span className="text-[11px] uppercase tracking-wider text-foreground-muted">
            {provider}
          </span>
        </div>
        <div className="mt-0.5 truncate font-mono text-[11px] text-foreground-muted">{model}</div>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="shrink-0 text-xs text-foreground-muted hover:text-destructive"
      >
        제거
      </button>
    </div>
  );
}

function Checkbox({
  id,
  checked,
  onChange,
  label,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <label htmlFor={id} className="flex items-center gap-2 text-sm select-none">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4"
      />
      <span>{label}</span>
    </label>
  );
}

function zodIssuesToMap(
  issues: ReadonlyArray<{ path: (string | number)[]; message: string }>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of issues) {
    if (issue.path.length > 0) out[String(issue.path[0])] = issue.message;
  }
  return out;
}
